import { useState } from 'react';
import { Send, Check, Copy, TriangleAlert } from 'lucide-react';
import { BottomSheet } from '@/components/ui/dialog';
import { haptic } from '@/lib/telegram';
import { reportError } from '@/lib/error-report';

type Phase = 'idle' | 'sending' | 'sent' | 'failed';

/**
 * Форма явной жалобы «Сообщить об ошибке» (kind=user_report). Открывается из
 * persistent-тоста в __root при 5xx/сетевой ошибке. Partner-webapp ru-only → строки хардкодом.
 */
export function ErrorReportSheet({
  error,
  open,
  onClose,
}: {
  error: unknown;
  open: boolean;
  onClose: () => void;
}) {
  const [description, setDescription] = useState('');
  const [phase, setPhase] = useState<Phase>('idle');
  const [code, setCode] = useState('');

  const submit = async () => {
    setPhase('sending');
    haptic('light');
    const res = await reportError(error, {
      kind: 'user_report',
      severity: 'error',
      description: description.trim() || undefined,
    });
    setCode(res.reportCode);
    setPhase(res.ok ? 'sent' : 'failed');
    haptic(res.ok ? 'success' : 'error');
  };

  const copyCode = () => {
    navigator.clipboard?.writeText(code).catch(() => {});
    haptic('light');
  };

  const close = () => {
    onClose();
    setTimeout(() => {
      setPhase('idle');
      setDescription('');
      setCode('');
    }, 300);
  };

  return (
    <BottomSheet open={open} onClose={close} title="Сообщить об ошибке">
      {phase === 'sent' || phase === 'failed' ? (
        <div className="space-y-4 pb-2 text-center">
          <div
            className="mx-auto w-14 h-14 rounded-full flex items-center justify-center"
            style={{
              background:
                phase === 'sent' ? 'rgba(255,255,255,0.07)' : 'rgba(248,113,113,0.14)',
            }}
          >
            {phase === 'sent' ? (
              <Check size={26} color="var(--color-primary)" />
            ) : (
              <TriangleAlert size={26} color="var(--color-danger)" />
            )}
          </div>
          <p className="text-sm text-muted-foreground">
            {phase === 'sent'
              ? 'Спасибо! Разработчики уже разбираются.'
              : 'Не удалось отправить. Сохраните код и назовите его в поддержке.'}
          </p>
          <CodeChip code={code} onCopy={copyCode} />
          <button
            type="button"
            onClick={close}
            className="w-full bg-primary text-primary-foreground py-3 rounded-xl text-sm font-bold active:scale-[0.98] transition-transform"
          >
            Закрыть
          </button>
        </div>
      ) : (
        <div className="space-y-4 pb-2">
          <p className="text-sm text-muted-foreground">
            Отправьте детали разработчикам, чтобы починили быстрее.
          </p>
          <textarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Опиши, что случилось (необязательно)"
            rows={3}
            maxLength={2000}
            className="surface-inset w-full rounded-xl px-4 py-3 text-sm text-foreground placeholder:text-white/35 resize-none focus:outline-none"
          />
          <button
            type="button"
            onClick={submit}
            disabled={phase === 'sending'}
            className="w-full bg-primary text-primary-foreground py-3 rounded-xl text-sm font-bold active:scale-[0.98] transition-transform flex items-center justify-center gap-2 disabled:opacity-60"
          >
            <Send size={16} />
            {phase === 'sending' ? 'Отправляю…' : 'Отправить отчёт разработчикам'}
          </button>
        </div>
      )}
    </BottomSheet>
  );
}

function CodeChip({ code, onCopy }: { code: string; onCopy: () => void }) {
  return (
    <div className="surface-inset rounded-xl px-4 py-3 text-left">
      <div className="eyebrow mb-1">Код отчёта</div>
      <div className="flex items-center justify-between gap-3">
        <span className="font-mono text-lg font-bold tracking-wider select-all">{code}</span>
        <button
          type="button"
          onClick={onCopy}
          className="flex items-center gap-1 text-xs text-foreground active:scale-95 transition-transform"
        >
          <Copy size={14} /> Скопировать
        </button>
      </div>
    </div>
  );
}
